import { Grid, InputAdornment, Link, TextField, Typography } from "@mui/material";
import React, { useState } from "react";
import { useWelcomeAssistantContext } from "../ContextType";
import { default as AccountCircle } from "@mui/icons-material/AccountCircle"
import { default as AlternateEmail } from "@mui/icons-material/AlternateEmail"
import { default as PhoneAndroid } from "@mui/icons-material/PhoneAndroid"
import { isNameValid, validateMail, validateMobile } from "../Validators";
import { Slider } from "../Slider";
import { FooterButtons } from "../FooterButtons";
import { styled } from "@mui/material/styles";
import { NewContact } from "../../../models/Contact";
import { ErlService } from "../../../service/ErlService";
import { ApiResponse } from "../../../models/ApiResponse";
import { fireEvent, trackLead } from "../../Tracking/FireEvent";
import { Label } from "../Label";

export function Stage2() {
    const {
        yourName, setYourName, yourMail, setYourMail, yourMobile, setYourMobile,
        setNewsletterConsent, newsletterConsent, equityCapitalState, monthlyInvestState,
        source
    } = useWelcomeAssistantContext();

    const [formSubmitted, setFormSubmitted] = useState(false);

    const isMailEmpty = !yourMail || yourMail.trim() === '';
    const isMobileEmpty = !yourMobile || yourMobile.trim() === '';

    const isMailValid = validateMail(yourMail);
    const isMobileValid = validateMobile(yourMobile);

    const isValid = () => {
        if (!isNameValid(yourName)) return false;

        // If mail is provided, it must be valid
        if (!isMailEmpty && !isMailValid) return false;

        // If mobile is provided, it must be valid
        if (!isMobileEmpty && !isMobileValid) return false;

        // At least one must be provided and valid
        if ((!isMailEmpty && isMailValid) || (!isMobileEmpty && isMobileValid)) {
            return true;
        }

        return false;
    }

    const saveInquiry = (resolve: any) => {

        const newContact = new NewContact(equityCapitalState.from, equityCapitalState.to, monthlyInvestState.from, monthlyInvestState.to, yourName, yourMail, yourMobile, newsletterConsent, source);

        (new ErlService).createInvestmentPartner(newContact)
            .then((apiResponse: ApiResponse) => {
                console.log("inquiry saved!", newContact);
                fireEvent('welcome-assistant-completed');
                trackLead(apiResponse.response.idSha256, 'Landingpage 1');
                resolve(true);
            })
            .catch(reason => {
                resolve(false);
                console.log(reason);
                return false;
            });

        return true;
    }

    const NewsletterConsentText = styled(Typography)(
        ({ theme }) => `
                font-size: 11px;
                line-height: 16px;
                color: #262527;
              `);

    const NewsletterConsentLink = styled(Link)(
        ({ theme }) => `
                font-size: 11px;
                line-height: 16px;
                opacity: 0.6;
                color: #404040;
                text-decoration: underline;
                cursor: pointer;
              `);

    const DataPrivacyNotice = styled(Typography)(
        ({ theme }) => `
                margin-bottom: 0;
                font-size: .8rem;
              `);

    if (equityCapitalState.isMinimum && monthlyInvestState.isMinimum) {
        return <Slider nr={2}>
            <Grid container rowGap={3} justifyContent="center">
                <Grid item xs={12}>
                    <Typography>
                        Leider ist es mit deinem Profil nicht möglich ein Immobilieninvestment zu tätigen.
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Typography>
                        Du solltest mindestens 10.000 € Eigenkapital mitbringen oder bereit sein,
                        mehr als 200 € im Monat in deinen Vermögensaufbau zu investieren.
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Typography>
                        Klicke auf “Zurück”, wenn du deine Angaben noch einmal korrigieren willst.
                    </Typography>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <FooterButtons forwardEnabled={false} />
            </Grid>
        </Slider>
    }

    return (
        <Slider nr={2}>

            <Grid container rowGap={3} justifyContent="center">

                <Grid item xs={12}>
                    <Typography>
                        Herzlichen Glückwunsch, Du erfüllst alle Voraussetzungen für ein Immobilieninvestment!
                    </Typography>
                    <Typography>
                        Bitte fülle jetzt folgende Felder aus und wir melden uns mit den nächsten Schritten bei Dir:
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <Label>Dein Name *</Label>
                    <TextField
                        error={!isNameValid(yourName) && formSubmitted}
                        helperText={!isNameValid(yourName) && formSubmitted ? 'Bitte deinen Namen eingeben' : null}
                        fullWidth={true}
                        size="small"
                        value={yourName}
                        variant="standard"
                        onChange={(e) => setYourName(e.target.value)}
                        InputProps={{
                            startAdornment:
                                <InputAdornment position="start">
                                    <AccountCircle />
                                </InputAdornment>,
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Label>Deine E-Mail-Adresse</Label>
                    <TextField
                        type={"email"}
                        error={((isMailEmpty && isMobileEmpty) || (!isMailEmpty && !isMailValid)) && formSubmitted}
                        helperText={((isMailEmpty && isMobileEmpty) || (!isMailEmpty && !isMailValid)) && formSubmitted ? 'Bitte eine gültige E-Mail eingeben' : null}
                        fullWidth={true}
                        size="small"
                        value={yourMail}
                        variant="standard"
                        onChange={(e) => setYourMail(e.target.value)}
                        InputProps={{
                            startAdornment:
                                <InputAdornment position="start">
                                    <AlternateEmail />
                                </InputAdornment>,
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Label>Deine Telefonnummer / WhatsApp</Label>
                    <TextField
                        type={"tel"}
                        error={((isMailEmpty && isMobileEmpty) || (!isMobileEmpty && !isMobileValid)) && formSubmitted}
                        helperText={((isMailEmpty && isMobileEmpty) || (!isMobileEmpty && !isMobileValid)) && formSubmitted ? 'Bitte eine gültige Mobilnummer eingeben' : null}
                        fullWidth={true}
                        size="small"
                        value={yourMobile}
                        variant="standard"
                        onChange={(e) => setYourMobile(e.target.value)}
                        InputProps={{
                            startAdornment:
                                <InputAdornment position="start">
                                    <PhoneAndroid />
                                </InputAdornment>,
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <DataPrivacyNotice>
                        Mit Klick auf "Los geht's" erklärst Du Dich mit unseren <Link href="https://www.erl.de/datenschutz/"
                            target="_blank">Datenschutzbestimmungen</Link> einverstanden.
                    </DataPrivacyNotice>
                </Grid>
                <Grid item xs={12}>
                    <FooterButtons onForward={() => {
                        return new Promise<boolean>((resolve, reject) => {
                            setFormSubmitted(true);

                            if (isValid()) {
                                saveInquiry(resolve);
                            } else {
                                return resolve(false);
                            }

                        });
                    }} />
                </Grid>
                <Grid item xs={12}>
                    <NewsletterConsentText>
                        Wir möchten dich gerne auch in Zukunft per E-Mail über Investitionsmöglichkeiten und Inhalten zu und über Immobilien informieren und dir
                        gegebenenfalls E-Mails zur Produkt- oder Unternehmensbewertung zusenden. Wenn du keine weiteren E-Mails dieser Art von uns erhalten
                        möchtest, kannst du dich jederzeit abmelden oder dem Versand mit sofortiger Wirkung widersprechen. Hierfür entstehen keine anderen
                        Kosten als die Übermittlungskosten nach den Basistarifen. Für einen schnell Widerspruch, klicke <NewsletterConsentLink
                            onClick={() => setNewsletterConsent(false)}>hier</NewsletterConsentLink>. </NewsletterConsentText>

                    {!newsletterConsent &&
                        <NewsletterConsentText>Dein Widerspruch wurde vorgemerkt. Klicke <NewsletterConsentLink
                            onClick={() => setNewsletterConsent(true)}>hier</NewsletterConsentLink> um deinen Widerspruch zurück zu
                            ziehen.</NewsletterConsentText>
                    }
                </Grid>
            </Grid>
        </Slider>
    );
}